import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getIncidents, addIncidentInvestigationNote, updateIncidentStatus,
  getSeverityBadge, timeUntilDeadline,
} from '../../services/complianceService';
import toast from 'react-hot-toast';

export default function AdminIncidents() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', severity: '' });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [commissionerRef, setCommissionerRef] = useState('');
  const [resolutionText, setResolutionText] = useState('');

  const load = () => {
    setLoading(true);
    getIncidents(filter)
      .then((res) => setIncidents(res.incidents || []))
      .catch((err) => toast.error(err.message || 'Could not load incidents'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter.status, filter.severity]);

  const handleAddNote = async () => {
    if (!noteText || noteText.length < 5) { toast.error('Note too short'); return; }
    setActionInProgress(true);
    try {
      await addIncidentInvestigationNote(selectedIncident.id, noteText);
      toast.success('Note added');
      setNoteText('');
      load();
      setSelectedIncident(null);
    } catch (err) { toast.error(err.message || 'Failed'); }
    setActionInProgress(false);
  };

  const handleMarkSubmitted = async () => {
    if (!commissionerRef) { toast.error('Provide Commissioner reference number'); return; }
    setActionInProgress(true);
    try {
      await updateIncidentStatus(selectedIncident.id, 'submitted_to_commissioner', { commissionerReferenceNumber: commissionerRef });
      toast.success('Marked as submitted to Commissioner');
      load();
      setSelectedIncident(null);
      setCommissionerRef('');
    } catch (err) { toast.error(err.message || 'Failed'); }
    setActionInProgress(false);
  };

  const handleClose = async () => {
    if (!resolutionText || resolutionText.length < 10) { toast.error('Resolution too short'); return; }
    setActionInProgress(true);
    try {
      await updateIncidentStatus(selectedIncident.id, 'closed', { resolution: resolutionText });
      toast.success('Incident closed');
      load();
      setSelectedIncident(null);
      setResolutionText('');
    } catch (err) { toast.error(err.message || 'Failed'); }
    setActionInProgress(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/admin')}
          className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">
          ← Admin dashboard
        </button>
      </div>

      <h1 className="font-serif text-3xl text-brand-dark mb-2">Incidents — Notifiable Occurrences</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Critical incidents must be reported to NSW Point to Point Transport Commissioner within set timeframes.
      </p>

      <div className="flex gap-2 mb-4 flex-wrap">
        <select className="form-input form-select flex-1 min-w-[150px]" value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="investigating">Investigating</option>
          <option value="pending_commissioner">Pending Commissioner</option>
          <option value="submitted_to_commissioner">Submitted to Commissioner</option>
          <option value="closed">Closed</option>
        </select>
        <select className="form-input form-select flex-1 min-w-[150px]" value={filter.severity}
          onChange={(e) => setFilter({ ...filter, severity: e.target.value })}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
        </select>
      </div>

      {loading && <div className="p-10 text-center text-muted">Loading...</div>}

      <div className="grid gap-3">
        {incidents.map((inc) => {
          const sev = getSeverityBadge(inc.severity);
          const deadline = inc.reportingDeadline && timeUntilDeadline(inc.reportingDeadline);
          return (
            <div key={inc.id} onClick={() => setSelectedIncident(inc)}
              className="p-4 rounded-[14px] bg-white border border-slate-200 cursor-pointer transition-all">
              <div className="flex justify-between items-start mb-1.5">
                <div>
                  <span className="font-bold text-brand-dark">{inc.referenceNumber}</span>
                  <span className="ml-2 text-[0.85rem] text-body">{inc.typeLabel}</span>
                </div>
                <div className="flex gap-1.5">
                  <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold"
                    style={{ background: sev.bg, color: sev.color }}>
                    {sev.label}
                  </span>
                  {deadline && (
                    <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold"
                      style={{
                        background: deadline.overdue ? '#fee2e2' : '#dbeafe',
                        color: deadline.overdue ? '#991b1b' : '#1e40af',
                      }}>{deadline.label}</span>
                  )}
                </div>
              </div>
              <div className="text-[0.85rem] text-body mb-1">
                {inc.description.length > 150 ? inc.description.substring(0, 150) + '...' : inc.description}
              </div>
              <div className="flex justify-between text-[0.78rem]">
                <span className="text-body">Status: {inc.status}</span>
                <span className="text-muted">By: {inc.reporterName} ({inc.reporterRole})</span>
              </div>
            </div>
          );
        })}
      </div>

      {selectedIncident && (
        <div className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-5"
          onClick={() => setSelectedIncident(null)}>
          <div className="max-w-[700px] w-full max-h-[90vh] overflow-auto bg-white rounded-2xl p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h2 className="m-0 font-serif">{selectedIncident.referenceNumber}</h2>
              <button onClick={() => setSelectedIncident(null)}
                className="bg-none border-0 text-2xl cursor-pointer text-muted">×</button>
            </div>

            <div className="text-[0.9rem] leading-relaxed mb-4">
              <div><strong>Type:</strong> {selectedIncident.typeLabel}</div>
              <div><strong>Severity:</strong> {selectedIncident.severity}</div>
              <div><strong>Status:</strong> {selectedIncident.status}</div>
              <div><strong>Reported by:</strong> {selectedIncident.reporterName} ({selectedIncident.reporterRole})</div>
              {selectedIncident.bookingReferenceNumber && (
                <div><strong>Booking:</strong> {selectedIncident.bookingReferenceNumber}</div>
              )}
              <div className="mt-3">
                <strong>Description:</strong>
                <div className="p-2.5 rounded-lg bg-slate-50 mt-1 whitespace-pre-wrap">
                  {selectedIncident.description}
                </div>
              </div>
              {selectedIncident.witnesses?.length > 0 && (
                <div className="mt-3">
                  <strong>Witnesses:</strong>
                  <ul className="mt-1 mb-0 pl-5">
                    {selectedIncident.witnesses.map((w, i) => <li key={i}>{w.name}{w.contact && ` · ${w.contact}`}</li>)}
                  </ul>
                </div>
              )}
              {selectedIncident.policeCalled && (
                <div className="mt-2">
                  <strong>Police:</strong> called{selectedIncident.policeReportNumber && ` (ref ${selectedIncident.policeReportNumber})`}
                </div>
              )}
              {selectedIncident.investigationNotes?.length > 0 && (
                <div className="mt-3">
                  <strong>Investigation notes:</strong>
                  {selectedIncident.investigationNotes.map((n, i) => (
                    <div key={i} className="p-2 rounded-lg bg-slate-50 mt-1.5 text-[0.85rem]">
                      <div className="text-body text-[0.75rem] mb-0.5">
                        {n.byName} ({n.byRole}) · {n.timestamp?._seconds ? new Date(n.timestamp._seconds * 1000).toLocaleString('en-AU') : '...'}
                      </div>
                      {n.note}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Add investigation note</label>
              <textarea className="form-input" rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              <button onClick={handleAddNote} disabled={actionInProgress}
                className="mt-1.5 px-4 py-2.5 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">
                Add note
              </button>
            </div>

            {selectedIncident.reportingDeadline && selectedIncident.status !== 'submitted_to_commissioner' && selectedIncident.status !== 'closed' && (
              <div className="form-group mt-4">
                <label className="form-label">Mark as submitted to Commissioner</label>
                <input className="form-input" placeholder="Commissioner reference number"
                  value={commissionerRef} onChange={(e) => setCommissionerRef(e.target.value)} />
                <button onClick={handleMarkSubmitted} disabled={actionInProgress || !commissionerRef}
                  className="mt-1.5 px-4 py-2.5 rounded-lg border-0 text-white cursor-pointer font-semibold"
                  style={{ background: '#4f46e5' }}>
                  Mark submitted
                </button>
              </div>
            )}

            {selectedIncident.status !== 'closed' && (
              <div className="form-group mt-4">
                <label className="form-label">Close incident with resolution</label>
                <textarea className="form-input" rows={2} value={resolutionText}
                  onChange={(e) => setResolutionText(e.target.value)}
                  placeholder="Resolution summary..." />
                <button onClick={handleClose} disabled={actionInProgress || !resolutionText}
                  className="mt-1.5 px-4 py-2.5 rounded-lg border-0 text-white cursor-pointer font-semibold"
                  style={{ background: '#dc2626' }}>
                  Close incident
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}